import { motion } from "framer-motion";
import { ExternalLink, LayoutGrid } from "lucide-react";
import { SandParticles } from "./SandParticles";
import { SITE, STORES } from "../data/site";

const tcg = STORES[0];

const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => (
  <section id="home" data-testid="hero-section" className="relative overflow-hidden">
    <div
      aria-hidden="true"
      className="absolute -top-48 left-1/2 h-[480px] w-[820px] -translate-x-1/2 rounded-full bg-amber-500/10 blur-[130px]"
    />
    <div
      aria-hidden="true"
      className="absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-yellow-600/10 blur-[110px]"
    />
    <SandParticles className="opacity-70" />
    <div className="relative mx-auto grid max-w-7xl gap-10 px-4 pb-16 pt-8 sm:px-6 md:pt-16 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-8 lg:px-8 lg:pb-24 lg:pt-20">
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="order-1 flex items-center justify-center lg:order-2"
      >
        <div className="relative flex items-center justify-center p-4 sm:p-8">
          <div
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-amber-500/10 blur-3xl"
          />
          <img
            src="/brand/logo-circular-t.png"
            alt="Sandstorm Trading circular logo"
            data-testid="hero-logo-img"
            width="800"
            height="810"
            className="relative h-48 w-auto animate-float sm:h-80"
          />
        </div>
      </motion.div>
      <div className="order-2 lg:order-1">
        <motion.p
          {...fade(0.05)}
          data-testid="hero-eyebrow"
          className="mb-5 inline-flex items-center rounded-full border border-amber-500/25 bg-amber-500/5 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-300"
        >
          Online Trading Card &amp; Collectibles Retailer
        </motion.p>
        <motion.h1
          {...fade(0.12)}
          data-testid="hero-heading"
          className="font-display text-4xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          Sandstorm
          <span className="gold-text block">Trading</span>
        </motion.h1>
        <motion.p
          {...fade(0.2)}
          data-testid="hero-tagline"
          className="mt-4 font-display text-lg font-semibold tracking-wide text-zinc-200"
        >
          Cards <span className="text-amber-400">•</span> Games{" "}
          <span className="text-amber-400">•</span> Collectibles
        </motion.p>
        <motion.p
          {...fade(0.28)}
          className="mt-5 max-w-xl text-base leading-relaxed text-zinc-400"
        >
          Shop our growing selection of trading cards and collectibles through our
          trusted online storefronts — with TCGPlayer as our primary marketplace.
        </motion.p>
        <motion.div {...fade(0.36)} className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <a
            href={tcg.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-shop-tcgplayer-cta"
            className="gold-btn px-8 py-4 text-sm"
          >
            Shop TCGPlayer
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
          <a
            href="#shop"
            data-testid="hero-view-stores-cta"
            className="ghost-btn px-8 py-4 text-sm uppercase tracking-wide"
          >
            <LayoutGrid className="h-4 w-4" aria-hidden="true" />
            View All Stores
          </a>
        </motion.div>
        <motion.div
          {...fade(0.44)}
          className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium uppercase tracking-widest text-zinc-500"
        >
          <span data-testid="hero-chip-established">
            Est. <span className="text-amber-400">{SITE.established}</span>
          </span>
          <span aria-hidden="true" className="h-1 w-1 rounded-full bg-amber-500/50" />
          <span data-testid="hero-chip-stores">4 Marketplace Storefronts</span>
          <span aria-hidden="true" className="h-1 w-1 rounded-full bg-amber-500/50" />
          <span data-testid="hero-chip-domain" className="font-mono normal-case tracking-normal">
            {SITE.domain}
          </span>
        </motion.div>
      </div>
    </div>
  </section>
);
